// A question asked of the model chat from somewhere else on the page.
//
// The same shape as `treeRefreshBus` and `summaryRefreshBus` beside it, and for
// the same reason: the panel is mounted once by `ModelPage` and the things that
// want to ask it something are rendered far below, with no prop path between
// them. A bus is also the smallest possible change to `ModelChatPanel`, which
// matters while several branches are rewriting its internals.

export type AskHandler = (question: string) => void;

export function createAskBus() {
  const handlers = new Set<AskHandler>();
  /**
   * A question asked before anything was listening.
   *
   * Held rather than dropped, because the models page now hands a question ACROSS a
   * navigation: it is emitted while the models list is still mounted, and the panel
   * that answers it subscribes on the model page a moment later, after its own feature
   * flag has resolved. Emitting into an empty bus would lose exactly the question the
   * reader asked us to carry, and it would look like the chip did nothing.
   *
   * One deep, and cleared on delivery: this is a handoff, not a queue.
   */
  let pending: string | null = null;
  return {
    subscribe(handler: AskHandler): () => void {
      handlers.add(handler);
      if (pending !== null) {
        const question = pending;
        pending = null;
        try {
          handler(question);
        } catch {
          // ignored on purpose, see emit
        }
      }
      return () => {
        handlers.delete(handler);
      };
    },
    emit(question: string) {
      if (handlers.size === 0) {
        pending = question;
        return;
      }
      // A handler added or removed during dispatch must not change this pass,
      // and one that throws must not stop the others.
      [...handlers].forEach((handler) => {
        try {
          handler(question);
        } catch {
          // ignored on purpose, see above
        }
      });
    },
    get size() {
      return handlers.size;
    },
  };
}

const bus = createAskBus();

export const onAsk = bus.subscribe;
export const emitAsk = (question: string) => bus.emit(question);

/**
 * A question handed from the models list to a model's own chat, across a FULL page
 * load.
 *
 * `isModelIdValid` is computed during server-side render (`renderView.tsx`), so a
 * model page reached by client-side routing has never had it computed and redirects
 * straight to 404. Every link into a model is therefore a real navigation, which is
 * why the in-page bus above cannot carry this on its own: the module is reloaded.
 *
 * sessionStorage rather than a query parameter: this is a sentence somebody typed, and
 * a sentence in a URL is in their history and in our access logs forever. Rather than
 * a cookie: it never needs to reach the server.
 *
 * Keyed by model, so a handoff meant for 57 cannot be replayed on 49, and read once.
 */
const HANDOFF_KEY = 'studio.handoff.ask';

export interface Handoff {
  /**
   * What the reader TYPED, and nothing else.
   *
   * A button press is a decision about the list, not a question about a model, and
   * carrying one over sent "Yes, archive models 64 through 70" into model 44's chat
   * on 2026-09-22, where the model agent had to explain it cannot archive anything.
   * It was right to refuse and should never have been asked. So a press leaves this
   * empty and the model opens with its own assistant idle.
   */
  question?: string;
  /**
   * The last thing the models-list assistant said, carried across the navigation.
   *
   * Without it the handoff drops the half of the conversation that explains why
   * anyone is here: the reader was told model 44 is the broken one and then arrived
   * on model 44 with an assistant that had never heard of the conversation.
   */
  carried?: string;
}

export function stashHandoff(modelId: string, handoff: Handoff): void {
  if (!handoff.question && !handoff.carried) return;
  try {
    sessionStorage.setItem(HANDOFF_KEY, JSON.stringify({ modelId, ...handoff }));
  } catch {
    // Private windows and blocked site data both throw here. The navigation still
    // happens and the reader retypes, which is the old behaviour rather than a break.
  }
}

/** What was handed to this model, once. Null for anything else. */
export function takeHandoff(modelId: string): Handoff | null {
  try {
    const raw = sessionStorage.getItem(HANDOFF_KEY);
    if (!raw) return null;
    sessionStorage.removeItem(HANDOFF_KEY);
    const held = JSON.parse(raw) as { modelId?: string } & Handoff;
    if (String(held.modelId) !== String(modelId)) return null;
    const question = held.question || undefined;
    const carried = held.carried || undefined;
    return question || carried ? { question, carried } : null;
  } catch {
    return null;
  }
}

/**
 * What to hand over, given where a conversation got to.
 *
 * Here rather than inside the component because this is the decision that broke, and
 * the components in this repo have no test harness at all (no enzyme, no testing
 * library), so a rule left in the JSX is a rule nothing can check. The caller passes
 * what it knows; every judgement about what crosses the boundary is made here.
 *
 * `lastTyped` is empty after a button press, and that is the whole guard against
 * sending a decision about the models list into one model's chat.
 */
export function handoffFrom(
  turns: readonly { role: string; text: string }[],
  lastTyped: string
): Handoff {
  // The last answer with anything IN it: the turn being streamed right now is an
  // empty agent turn, and handing over "" would drop the context silently.
  const answered = [...turns]
    .reverse()
    .find((turn) => turn.role === 'agent' && turn.text.trim());
  return {
    question: lastTyped.trim() || undefined,
    carried: answered?.text.trim() || undefined,
  };
}

/**
 * The opening turn for a model's chat, given what came over with the navigation.
 *
 * The carried text is ATTRIBUTED rather than pasted. Handed over bare it reads as
 * the reader's own words, and an assistant that believes the reader said "model 44
 * is not separating at all" will defend it as their claim rather than check it.
 *
 * Null when there is nothing to ask. Context alone does not open a turn: it would
 * spend a model call to tell the assistant something before anyone has asked it
 * anything, and the reader arrived to look at a page, not to be talked at.
 */
export function handoffMessage(handoff: Handoff | null): string | null {
  if (!handoff?.question) return null;
  if (!handoff.carried) return handoff.question;
  return (
    `Continuing from the Data Studio assistant, which just said:\n\n` +
    `${handoff.carried.trim()}\n\n` +
    handoff.question
  );
}
